import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

/* ============================================================================
 * ТЕМЫ ОФОРМЛЕНИЯ
 *
 * pilot       — СВЕТЛАЯ тема, панель пилота
 * situational — ТЁМНАЯ тема, Ситуационный центр (Air Boss):
 *               фон #0F172A, текст #E2E8F0, критическое предупреждение #EF4444
 *
 * Значения берутся из design-tokens.json → раздел color (см. styles/theme.css).
 * Переключение в рантайме, состояние сохраняется в localStorage.
 * ========================================================================= */

export type UtTheme = 'pilot' | 'situational'

export const THEME_STORAGE_KEY = 'ut-theme'

export const THEME_LABELS: Record<UtTheme, { name: string; hint: string }> = {
  pilot: { name: 'Панель пилота', hint: 'Светлая тема · высокая читаемость' },
  situational: { name: 'Ситуационный центр', hint: 'Тёмная тема · Air Boss' },
}

interface ThemeContextValue {
  theme: UtTheme
  setTheme: (t: UtTheme) => void
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

function readInitialTheme(): UtTheme {
  if (typeof window === 'undefined') return 'situational'
  try {
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (saved === 'pilot' || saved === 'situational') return saved
  } catch {
    // localStorage может быть недоступен (приватный режим) — не критично
  }
  return 'situational'
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<UtTheme>(readInitialTheme)

  // Применяем тему к корневому элементу — CSS-переменные переключаются мгновенно
  useEffect(() => {
    const root = document.documentElement
    root.setAttribute('data-ut-theme', theme)
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme)
    } catch {
      // Игнорируем ошибку записи в localStorage
    }
  }, [theme])

  const setTheme = useCallback((t: UtTheme) => setThemeState(t), [])
  const toggleTheme = useCallback(
    () => setThemeState((prev) => (prev === 'pilot' ? 'situational' : 'pilot')),
    [],
  )

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

/** Доступ к теме; вне провайдера — тема по умолчанию */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  const fallback = useMemo<ThemeContextValue>(
    () => ({ theme: 'situational', setTheme: () => undefined, toggleTheme: () => undefined }),
    [],
  )
  return ctx ?? fallback
}
